import React, { useState, useRef } from 'react'
import uuid from 'uuid'
import AceEditor from 'react-ace'
import {
  FileUpload,
  Button,
  Icon,
  Input,
  useTraversal,
  Select,
} from '@guillotinaweb/react-gmi'

import { RichTextEditor } from '../../../components/RichTextEditor'
import Confirm from '../../../components/confirm'
import { AccordionCard } from '../../../components/accordion-card'
import { DnD } from '../../../common/dnd'
import { I18NField } from '../../../components/i18nfield'
import { PreviewWebsites } from './Preview'
import { ProductSelector } from '../../../components/product-selector'
import { QueryProducts } from '../../../components/query-products'
import { isProd, setLangs } from '../../../consts'
import {
  allowToMoveOnlyInAccordionHeader,
  useClipboard,
  setMultiLangContent,
} from '../../../utils'
import { usePatchLanding } from './usePatchLanding'

const HISTORIAL_MAX = 20
const Empty = () => null
const cdnPrefix = isProd() ? '' : 'stage-'
const cdn = `https://${cdnPrefix}cdn.vinissimus.com/img/unsafe/keep/plain/local://`
const emptyLangs = setLangs('')
const epoch = () => new Date().getTime()
const templates = [
  { text: 'Carousels', value: 'carousels' },
  { text: 'Text + Image', value: 'text-image' },
  { text: 'HTML', value: 'just-html' },
  { text: 'HTML (deprecated)', value: 'html' },
  { text: 'Product list', value: 'product-list' },
  { text: 'Blog Product list', value: 'blog-product-list' },
  { text: 'View more Button', value: 'view-more-button' },
  { text: 'View more Anchor', value: 'view-more-anchor' },
  { text: 'Email Signup Form', value: 'email-signup' },
]
const templatesAsObj = templates.reduce((o, c) => {
  o[c.value] = c.text
  return o
}, {})

export function Sections({ state }) {
  const key = useRef(epoch())
  const Ctx = useTraversal()
  const [confirmation, setConfirmation] = useState(null)
  const [clipboard, setClipboard] = useClipboard('copy-landing')
  const [loading, saveLanding] = usePatchLanding()
  const [sections, setSections] = useState(state.context.sections)
  const [historial, setHistorial] = useState([sections])
  const lastUndo = useRef()
  const prevSectionsRef = useRef(sections)

  React.useEffect(() => {
    if (state.context.sections !== prevSectionsRef.current) {
      setSections(
        state.context.sections.map((s) => ({ ...s, id: s.id || uuid() }))
      )
      prevSectionsRef.current = state.context.sections
    }
  }, [state.context.sections])

  function copy(el) {
    sessionStorage.setItem('copy-landing', JSON.stringify(el))
    setClipboard(el)
    Ctx.flash('Copied!', 'success')
  }

  function paste() {
    const newSection = { ...clipboard, id: uuid() }
    setSections((s) => [...s, newSection])
    sessionStorage.removeItem('copy-landing')
    setClipboard(null)
  }

  function updateSections(newSections) {
    if (Date.now() - (lastUndo.current || 0) < 100) return
    setSections(newSections)
    setHistorial((prev) => [newSections, ...prev].slice(0, HISTORIAL_MAX))
  }

  function onChangeSection(newSection) {
    setSections((prevSections) =>
      prevSections.map((oldSection) =>
        oldSection.id === newSection.id ? newSection : oldSection
      )
    )
  }

  function undo() {
    const [, lastChange, ...hist] = historial
    lastUndo.current = Date.now()
    setHistorial([lastChange, ...hist])
    setSections(lastChange)
  }

  function onRemoveSection(sectionId) {
    updateSections(sections.filter(({ id }) => id !== sectionId))
  }

  function reset() {
    key.current = epoch()
    updateSections(state.context.sections)
  }

  function add() {
    const emptySection = {
      id: uuid(),
      template: 'carousels',
      title: emptyLangs,
      description: emptyLangs,
      alt: emptyLangs,
      text: emptyLangs,
      slug: emptyLangs,
      products: {
        type: 'group',
        value: [],
      },
      query: {},
      image_src: null,
      collapsed: false,
    }
    updateSections([...sections, emptySection])
  }

  return (
    <I18NField Widget={Empty}>
      {(lang) => (
        <>
          <PreviewWebsites currentLang={lang} data={state.context} />
          {sections.length === 0 && (
            <div className="empty-sections">There aren't sections yet.</div>
          )}
          <DnD
            onChange={updateSections}
            list={sections}
            events={allowToMoveOnlyInAccordionHeader}
            key={key.current}
            getChild={(data) => (
              <Section
                copy={copy}
                data={data}
                lang={lang}
                onChange={onChangeSection}
                onRemove={setConfirmation}
              />
            )}
          />
          <Confirm
            open={confirmation !== null}
            no={() => setConfirmation(null)}
            yes={() => {
              onRemoveSection(confirmation)
              setConfirmation(null)
            }}
          >
            Are you sure to remove this section?
          </Confirm>

          <div className="landings-btn-controls sections">
            <div className="secondary-btn-controls">
              <Button
                onClick={paste}
                type="button"
                disabled={!clipboard}
                className="is-normal is-outlined is-primary"
              >
                <Icon icon="fas fa-paste" />
              </Button>
              <Button
                onClick={add}
                type="button"
                className="is-outlined is-primary"
              >
                + Add new Section
              </Button>
              <Button
                disabled={loading || historial.length <= 1}
                onClick={undo}
                type="button"
                className="is-normal"
              >
                Undo
              </Button>
              <Button
                disabled={loading}
                onClick={reset}
                type="button"
                className="is-normal"
              >
                Reset
              </Button>
            </div>
            <hr />
            <Button
              loading={loading}
              onClick={() => saveLanding({ sections })}
              className="is-primary"
            >
              SAVE AND PUBLISH
            </Button>
          </div>
        </>
      )}
    </I18NField>
  )
}

function Section({ data, onChange, onRemove, lang, copy }) {
  const Ctx = useTraversal()
  const [uploading, setUploading] = useState(false)
  const sectionTitle = data.title?.[lang] || data.module_id || 'Untitled'
  const title = `${sectionTitle} | ${templatesAsObj[data.template]}`

  function onUploadImg(callback) {
    async function uploader(file) {
      setUploading(true)
      const res = await Ctx.client.upload(Ctx.path, file, `landings/${data.id}`)

      if (res.status >= 300) {
        alert('Error uploading the image')
        setUploading(false)
        return
      }

      const { path } = await res.json()
      callback(`/${path}`)
      setUploading(false)
    }
    return uploader
  }

  const handleInputChange = (field, value) => {
    const newData = {
      ...data,
      [field]: {
        ...(data[field] || {}),
        [lang]: typeof value === 'object' ? value.target.value : value,
      },
    }
    onChange(newData)
  }

  let productTypes = [
    { value: 'groups', text: 'Lista de grupos de productos' },
    { value: 'referencias', text: 'Lista de referencias de productos' },
  ]

  let carrouselTypes = [
    { value: 'group', text: 'Lista de grupos de productos' },
    { value: 'refs', text: 'Lista de referencias de productos' },
    { value: 'query', text: 'Query de búsqueda' },
  ]

  return (
    <AccordionCard
      key={data.collapsed}
      onToggle={(collapsed) => onChange({ ...data, collapsed })}
      defaultCollapsed={data.collapsed}
      title={
        <div className="header-title">
          <div>{title}</div>
          <div>
            <span onClick={() => copy(data)} title="Copy">
              <Icon icon="fas fa-copy" />
            </span>
            <span onClick={() => onRemove(data.id)} title="Delete">
              <Icon icon="fas fa-trash" />
            </span>
          </div>
        </div>
      }
      className="template-section"
    >
      <div className="fix-data">
        <label className="label">Template</label>
        <Select
          onChange={(e) =>
            onChange({
              ...data,
              template: e.target.value,
              products: data.products || {
                type: 'group',
                value: [],
              },
              title: data.title || emptyLangs,
              description: data.description || emptyLangs,
              slug: data.slug || emptyLangs,
              alt: data.alt || emptyLangs,
            })
          }
          value={data.template || ''}
          options={templates}
        />
        {['carousels', 'html', 'product-list', 'blog-product-list'].includes(
          data.template
        ) && (
          <>
            <label className="label">
              Title{' '}
              <span className="has-text-grey-light is-size-7"> (optional)</span>
            </label>
            <Input
              id="title"
              value={data.title?.[lang] || ''}
              onChange={(e) => handleInputChange('title', e)}
            />
            <label className="label">
              Description
              {['carousels', 'blog-product-list'].includes(data.template) && (
                <span className="has-text-grey-light is-size-7">
                  {' '}
                  (optional)
                </span>
              )}
            </label>
            <RichTextEditor
              key={lang}
              id={`${data.id}-description${lang}`}
              value={data.text?.[lang] || ''}
              onChange={(value) => handleInputChange('text', value)}
            />
          </>
        )}
        {
          // TEXT (html) + IMAGE (both optional)
          data.template === 'text-image' && (
            <>
              <label className="label">Text</label>
              <RichTextEditor
                key={lang}
                id={`${data.id}-description${lang}`}
                value={data.text?.[lang] || ''}
                onChange={(value) => handleInputChange('text', value)}
              />
            </>
          )
        }
      </div>

      {(() => {
        // BLOG PRODUCT LIST
        if (data.template === 'blog-product-list') {
          const type = data.query?.referencias ? 'referencias' : 'groups'
          const typeSelector = data.query?.referencias ? 'refs' : 'group'
          const defaultQuery = {
            opciones: { estado: ['selling', 'notavailable'] },
            serializer: 'full',
          }

          return (
            <div className="section-data">
              <label className="label"></label>
              <Select
                onChange={(e) =>
                  onChange({
                    ...data,
                    query: {
                      ...defaultQuery,
                      [e.target.value]: [],
                    },
                    type: e.target.value,
                  })
                }
                value={type}
                options={productTypes}
              />
              <label className="label">Products</label>
              <ProductSelector
                key={typeSelector}
                id={data?.slug?.es}
                type={typeSelector}
                products={data.query?.[type] || []}
                onChange={(products) =>
                  onChange({
                    ...data,
                    query: {
                      ...defaultQuery,
                      [type]: products,
                    },
                  })
                }
              />
            </div>
          )
        }
      })()}
      {
        // CAROUSELS DATA
        data.template === 'carousels' && (
          <div className="section-data">
            <label className="label">
              Carousel image{' '}
              <span className="has-text-grey-light is-size-7">(optional)</span>
            </label>
            <p class="note-text">
              min-width: 150px; max-height: 330px (vert) or proportional to
              height: 330px.
            </p>
            {data.image_src && (
              <img
                className="preview"
                alt="img"
                src={cdn + data.image_src[lang]}
              />
            )}
            <FileUpload
              key={`upload-image-carousels-${lang}`}
              accept="image/*"
              onChange={onUploadImg((path) =>
                onChange({
                  ...data,
                  image_src: setMultiLangContent(path, lang, data.image_src),
                })
              )}
              label={uploading ? 'Uploading...' : 'Upload an image'}
            />
            <label className="label">Value type</label>
            <Select
              onChange={(e) =>
                onChange({
                  ...data,
                  products: {
                    ...data.products,
                    type: e.target.value,
                    value: null,
                  },
                })
              }
              value={data.products.type || ''}
              options={carrouselTypes}
            />
            {(data.products.type === 'group' ||
              data.products.type === 'refs') && (
              <>
                <label className="label">Carousel products</label>
                <ProductSelector
                  id={data?.slug?.es}
                  type={data.products.type}
                  products={data.products?.value || []}
                  onChange={(products) =>
                    onChange({
                      ...data,
                      products: { ...data.products, value: products },
                    })
                  }
                />
              </>
            )}
            {data.products.type === 'query' && (
              <QueryProducts
                query={data.products.value}
                title="Carousel query"
                onChange={(value) =>
                  onChange({
                    ...data,
                    products: { ...data.products, value },
                  })
                }
              />
            )}
          </div>
        )
      }

      {
        // PRODUCT LIST
        data.template === 'product-list' && (
          <div className="section-data">
            <QueryProducts
              query={data.query}
              title="Query"
              onChange={(query) => onChange({ ...data, query })}
            />
          </div>
        )
      }

      {
        // JUST HTML
        data.template === 'just-html' && (
          <AceEditor
            mode="html"
            key={lang}
            theme="textmate"
            value={data.html?.[lang]}
            onChange={(html) =>
              onChange({
                ...data,
                html: {
                  ...(data.html || {}),
                  [lang]: html,
                },
              })
            }
            width="100%"
            name="HTML_editor"
            fontFamily="Menlo, Consolas, Ubuntu Mono"
            editorProps={{
              $blockScrolling: Infinity,
            }}
          />
        )
      }

      {
        // DEPRECATED: IMAGE + HTML
        data.template === 'html' && (
          <div className="section-data">
            <RichTextEditor
              key={lang}
              id={`comment_${lang}`}
              value={(data.html || {})[lang] || ''}
              onChange={(value) => {
                onChange({
                  ...data,
                  html: {
                    ...(data.html || {}),
                    [lang]: value,
                  },
                })
              }}
            />
            <label className="label">Image</label>
            <p class="note-text">max-width: 300px; max-height: 200px</p>
            {data.image_src && (
              <img
                className="preview"
                alt="img"
                src={cdn + data.image_src.es}
              />
            )}
            <FileUpload
              accept="image/*"
              onChange={onUploadImg((path) =>
                onChange({ ...data, image_src: setLangs(path) })
              )}
              label={uploading ? 'Uploading...' : 'Upload an image'}
            />
            <Input
              id="img_alt"
              placeholder="Alt"
              value={data.alt?.[lang] || ''}
              onChange={(e) => handleInputChange('alt', e)}
            />
          </div>
        )
      }

      {
        // Text + Image
        data.template === 'text-image' && (
          <div className="section-data">
            <label className="label">Image</label>
            <p class="note-text">
              Free size. (Left and Right position, max size recommended:
              400x400. For Top and Bottom position and big images, max-width
              recommended: 1200px)
            </p>
            {data.img_src && (
              <img
                className="preview"
                alt="img"
                src={cdn + data.img_src[lang]}
              />
            )}
            <FileUpload
              accept="image/*"
              key={`file-upload-${lang}`}
              onChange={onUploadImg((path) =>
                onChange({
                  ...data,
                  img_src: setMultiLangContent(path, lang, data.img_src),
                })
              )}
              label={uploading ? 'Uploading...' : 'Upload an image'}
            />
            <label className="label">Caption</label>
            <RichTextEditor
              key={lang}
              id={`${data.id}-caption${lang}`}
              value={(data.img_caption || {})[lang] || ''}
              onChange={(value) => {
                onChange({
                  ...data,
                  img_caption: {
                    ...(data.img_caption || {}),
                    [lang]: value,
                  },
                })
              }}
            />
            <label className="label">Position</label>
            <Select
              onChange={(e) =>
                onChange({ ...data, img_position: e.target.value })
              }
              value={data.img_position || 'left'}
              options={[
                { value: 'left', text: 'Left' },
                { value: 'top', text: 'Top' },
                { value: 'bottom', text: 'Bottom' },
                { value: 'right', text: 'Right' },
              ]}
            />
            <Input
              id="img_alt"
              placeholder="Alt"
              value={data.img_alt?.[lang] || ''}
              onChange={(e) => handleInputChange('img_alt', e)}
            />
            <Input
              id="img_link"
              placeholder="Link"
              value={data.img_link?.[lang] || ''}
              onChange={(e) => handleInputChange('img_link', e)}
            />
          </div>
        )
      }

      {
        // EMAIL SIGNUP FORM - Versión simplificada
        data.template === 'email-signup' && (
          <div className="section-data">
            <p className="note-text">
              Este componente utilizará el formulario de registro predefinido
              del frontend.
            </p>
          </div>
        )
      }
    </AccordionCard>
  )
}
